import React, { ReactElement, useEffect, useState } from 'react';
import SiteHead from 'components/site-head';
import {
  classNames,
  formatFromSeconds,
  minutesAgo,
  minutesSince,
} from 'lib/util';
import { useFirestoreQuery } from 'lib/useFirestoreQuery';
import { db } from 'lib/firebase';
import { collection, orderBy, query, where } from 'firebase/firestore';
import NavBar from 'components/nav-bar';
import Link from 'next/link';
import FriendlyError from 'components/friendly-error';
import { FileDoc } from 'lib/model';
import { format } from 'date-fns';
import { LoadMoreRow } from 'components/load-more-row';
import { SearchIcon } from '@heroicons/react/outline';
import {
  ExclamationCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/solid';
import { useDebounce } from 'lib/useDebounce';
import TranscriptRowMenu from 'components/transcript/transcript-row-menu';
import FileDeleteDialog from 'components/file/file-delete-dialog';
import { useCurrentUser } from 'lib/useCurrentUser';
import { postAuthed } from 'lib/fetch';
import FileUploadButton from 'components/file/file-upload-button';
import FileTranscribeDialog from 'components/file/file-transcribe-dialog';
import { getLanguages, LanguageMap } from 'lib/languages';
import { GetStaticProps } from 'next';
import { useInterval } from 'lib/useInterval';
import FileUploadFromDriveButton from 'components/file/file-upload-from-drive-button';
import { FileUploadProgress } from 'components/file/file-upload-progress';
import { ProviderId } from 'firebase/auth';

interface Props {
  languages: LanguageMap;
}

Transcripts.requiresAuth = true;

export default function Transcripts(props: Props): ReactElement {
  const [search, setSearch] = useState('');
  const searchDebounced = useDebounce(search.trim());
  const { user, authUser, userError } = useCurrentUser();

  const {
    data: allFiles,
    error: fileError,
    moreLoader,
  } = useFirestoreQuery<FileDoc>(
    user &&
      query(
        collection(db, `users/${user.id}/files`),
        where('deleted', '==', null),
        orderBy('created', 'desc')
      ),
    25
  );

  useInterval(
    async () => {
      allFiles
        .filter((f) => f.uploading && f.created && minutesSince(f.created) > 10)
        .forEach((f) =>
          postAuthed(
            'reportFileUploadTimedOut',
            { driveFileId: f.id },
            authUser
          )
        );

      allFiles
        .filter(
          (f) =>
            f.transcriptStatus === 'in_progress' &&
            f.transcriptRequested && // null till write hits server
            minutesSince(f.transcriptRequested) >
              Math.max(20, f.durationSeconds / 60)
        )
        .forEach((f) =>
          postAuthed('reportPendingTranscript', { driveFileId: f.id }, authUser)
        );
    },
    allFiles ? 60 * 1000 : null
  );

  const filteredFiles = allFiles?.filter(
    (f) =>
      searchDebounced.length == 0 ||
      (f.name || '')
        .toLocaleLowerCase()
        .includes(searchDebounced.toLocaleLowerCase())
  );

  useEffect(() => {
    if (searchDebounced.length > 0) {
      moreLoader.loadMore();
    }
  }, [searchDebounced, moreLoader]);

  const [transcribeDialogOpen, setTranscribeDialogOpen] = useState(false);
  const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileDoc>();

  function FileRow({ file }: { file: FileDoc }): ReactElement {
    return (
      <tr
        className={classNames(
          'hover:bg-gray-50',
          file.added && 'animate-highlight'
        )}
      >
        <td className="px-6 whitespace-nowrap truncate">
          <div>
            {!file.transcriptStatus || file.transcriptStatus === 'done' ? (
              <Link href={'/transcript?file=' + file.ref.id}>
                <a className="text-blue-500 hover:text-blue-700">{file.name}</a>
              </Link>
            ) : (
              <>
                <p>{file.name}</p>
                {file.error && (
                  <p className="inline-flex items-center text-red-500">
                    <ExclamationCircleIcon className="mr-1 w-5 h-5" />
                    {file.userError
                      ? file.error
                      : 'There was a problem uploading this file, please try again'}
                  </p>
                )}
                {file.transcriptStatus === 'error' && (
                  <p className="inline-flex items-center text-red-500">
                    <ExclamationCircleIcon className="mr-1 w-5 h-5" />
                    There was a problem transcribing this file
                  </p>
                )}
                {!file.error && file.transcriptStatus === 'not_requested' && (
                  <p className="inline-flex items-center text-sm italic">
                    <InformationCircleIcon className="mr-1 w-4 h-4 text-blue-600" />
                    If not transcribed within 7 days upload will be discarded
                  </p>
                )}
                {!file.error && file.transcriptStatus === 'in_progress' && (
                  <p className="inline-flex items-center text-sm italic">
                    <InformationCircleIcon className="mr-1 w-4 h-4 text-blue-600" />
                    File is being transcribed, please check back later
                  </p>
                )}
              </>
            )}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          {file.uploading ? (
            <FileUploadProgress file={file} />
          ) : file.deleteTriggered > minutesAgo(1) && !file.deleteError ? (
            <div className="h-10 w-10 py-2 text-red-600">Deleting...</div>
          ) : file.error || file.deleteError ? (
            <div className="h-10 w-10">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-40 sm:w-auto sm:text-sm"
                onClick={() => {
                  setSelectedFile(file);
                  setDeleteFileDialogOpen(true);
                }}
              >
                Delete
              </button>
            </div>
          ) : file.transcriptStatus === 'not_requested' ||
            file.transcriptStatus === 'error' ? (
            <div className="flex items-center">
              <button
                type="button"
                className="mr-1 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-40 sm:w-auto sm:text-sm"
                onClick={() => {
                  setSelectedFile(file);
                  setTranscribeDialogOpen(true);
                }}
              >
                Transcribe
              </button>
              <TranscriptRowMenu
                onDelete={() => {
                  setSelectedFile(file);
                  setDeleteFileDialogOpen(true);
                }}
              />
            </div>
          ) : file.transcriptStatus === 'in_progress' ? (
            <div className="h-10 w-10 py-2">Transcribing...</div>
          ) : (
            <div className="flex items-center">
              <Link href={'/transcript?file=' + file.ref.id}>
                <a className="mr-4 inline-flex items-center font-bold text-blue-500 hover:text-blue-700">
                  View
                </a>
              </Link>
              <TranscriptRowMenu
                onDelete={() => {
                  setSelectedFile(file);
                  setDeleteFileDialogOpen(true);
                }}
                onTranscribe={
                  file.uploaded &&
                  (() => {
                    setSelectedFile(file);
                    setTranscribeDialogOpen(true);
                  })
                }
              />
            </div>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900">
            {file.created && format(file.created, 'PPpp')}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900">
            {file.durationSeconds && formatFromSeconds(file.durationSeconds)}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      <SiteHead title="Transcripts" description="TranscribeKit Transcripts" />
      <NavBar />
      <header className="bg-white">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 flex gap-5">
          <h1 className="text-3xl font-bold text-gray-900">Transcripts</h1>
          <FileUploadButton user={user} />
          {authUser?.providerData.find(
            (p) => p.providerId === ProviderId.GOOGLE
          ) && <FileUploadFromDriveButton user={user} authUser={authUser} />}
        </div>
      </header>
      {user && selectedFile && props.languages && (
        <FileTranscribeDialog
          open={transcribeDialogOpen}
          setOpen={setTranscribeDialogOpen}
          file={selectedFile}
          user={user}
          authUser={authUser}
          languages={props.languages}
        />
      )}
      {user && selectedFile && (
        <FileDeleteDialog
          open={deleteFileDialogOpen}
          setOpen={setDeleteFileDialogOpen}
          file={selectedFile}
          user={user}
          authUser={authUser}
        />
      )}
      <main>
        {userError || fileError ? (
          <FriendlyError />
        ) : (
          <>
            <div className="max-w-5xl mx-auto mb-10 px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="max-w-sm mb-2">
                      <label htmlFor="default-search" className="sr-only">
                        Search
                      </label>
                      <div className="relative">
                        <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none text-gray-500">
                          <SearchIcon className="h-5 w-5" aria-hidden="true" />
                        </div>
                        <input
                          type="text"
                          id="default-search"
                          className="block pl-10 w-full text-sm rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Type here to search by file name"
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                        />
                        {search && (
                          <div className="absolute inset-y-0 right-0 flex items-center">
                            <label htmlFor="clear-search" className="sr-only">
                              Clear Search
                            </label>
                            <button
                              type="button"
                              id="clear-search"
                              className="h-full rounded-md border-transparent bg-transparent py-0 px-2 text-gray-500 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              onClick={() => setSearch('')}
                            >
                              <XCircleIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="table-fixed w-full divide-y divide-gray-200">
                        <colgroup>
                          <col />
                          <col className="w-40" />
                          <col className="w-0 md:w-48" />
                          <col className="w-0 md:w-32" />
                        </colgroup>
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              File Name
                            </th>
                            <th scope="col"></th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide"
                            >
                              Date
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Duration
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredFiles ? (
                            filteredFiles.length > 0 ? (
                              <>
                                {filteredFiles.map((file) => (
                                  <FileRow key={file.id} file={file} />
                                ))}
                                <LoadMoreRow moreLoader={moreLoader} />
                              </>
                            ) : (
                              <tr>
                                <td
                                  className="px-6 py-4 whitespace-nowrap"
                                  colSpan={4}
                                >
                                  {allFiles.length === 0 ? (
                                    <div className="text-center text-sm text-gray-900">
                                      {
                                        "You don't have any transcripts yet (or you are offline)"
                                      }
                                    </div>
                                  ) : (
                                    <div className="text-center text-sm text-gray-900">
                                      No transcripts found{' '}
                                      <a
                                        className="text-blue-500 hover:text-blue-700 hover:cursor-pointer"
                                        onClick={() => setSearch('')}
                                      >
                                        (Clear search)
                                      </a>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )
                          ) : (
                            <tr className="animate-pulse">
                              <td className="px-6 py-4">
                                <div className="h-5 bg-gray-200 rounded" />
                              </td>
                              <td className="px-6 py-4">
                                <div className="h-5 bg-gray-200 rounded" />
                              </td>
                              <td className="px-6 py-4">
                                <div className="h-5 bg-gray-200 rounded" />
                              </td>
                              <td className="px-6 py-4">
                                <div className="h-5 bg-gray-200 rounded" />
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </>
  );
}

export const getStaticProps: GetStaticProps<Props> = async () => ({
  props: {
    languages: await getLanguages(),
  },
  revalidate: 24 * 60 * 60, // 24 hours
});
